'use client';

import { ModalDownloadButton } from 'components/collection/modal-viewer/controls/download-button';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import {
	useRef,
	useState,
	useEffect,
	type SetStateAction,
	type Dispatch,
} from 'react';
import LoadingIcon from 'assets/icons/loading-spinner.svg';
import ModalCloseButton from 'components/collection/modal-viewer/controls/close-button';
import PaginationControls from 'components/collection/modal-viewer/controls/pagination-controls';
import RightsRestrictedMessage from 'components/collection/rights-restricted-message';
import styles from './modal-collection-viewer.module.scss';
import modalStyles from 'components/collection/modal-viewer/modal.module.scss';
import ZoomControls from 'components/collection/modal-viewer/controls/zoom-controls';
import ModalBackground from 'components/collection/modal-viewer/modal-background';
import type { KeyboardEvent } from 'react';
import { type ImageViewerImage } from '../../image-viewer';

const ModalCollectionViewer = ({
	images,
	activeIndex,
	onIndexChange,
	onClose,
	isRightToLeft,
	objectID,
	objectTitle,
	forceNoDownloadButton = false,
	forceNoZoom = false,
}: IProps) => {
	const [imageLoading, setImageLoading] = useState(true);
	const activeImage = images[activeIndex];
	const imageRefs = useRef([]);
	const nextButtonRef = useRef(null);
	const previousButtonRef = useRef(null);
	const showNextButton = activeIndex < images.length - 1;
	const showPreviousButton = activeIndex > 0;

	const handleKeyDown = (e: KeyboardEvent) => {
		switch (e.key) {
			case 'Escape':
				onClose();
				break;
			case 'End':
			case 'ArrowLeft':
				e.preventDefault();
				isRightToLeft ? handleForwardArrowClick() : handleBackArrowClick();
				break;
			case 'ArrowRight':
			case 'Home':
				e.preventDefault();
				isRightToLeft ? handleBackArrowClick() : handleForwardArrowClick();
				break;
		}
	};

	const handleImageLoaded = () => {
		setImageLoading(false);
	};

	const iterateSlides = (forward = true) => {
		const newIndex = forward
			? Math.min(activeIndex + 1, images.length - 1)
			: Math.max(activeIndex - 1, 0);
		imageRefs?.current?.[newIndex]?.scrollIntoView({ behavior: 'smooth' }); // https://www.youtube.com/watch?v=6Whgn_iE5uc

		if (newIndex !== activeIndex) {
			setImageLoading(true);
			onIndexChange(newIndex);
		}
	};

	// arrow click handlers
	const handleBackArrowClick = () => {
		iterateSlides(false);
	};
	const handleForwardArrowClick = () => {
		iterateSlides(true);
	};

	useEffect(() => {
		imageRefs?.current?.[activeIndex]?.scrollIntoView();
	}, [activeIndex, images]);

	return (
		<ModalBackground>
			<div className={styles.modal} onKeyDown={handleKeyDown}>
				{imageLoading && (
					<div className={styles.loading}>
						<LoadingIcon />
					</div>
				)}
				<TransformWrapper
					disablePadding={true}
					maxScale={forceNoZoom ? 1 : activeImage.isOpenAccess ? 5 : 1}
					wheel={{ smoothStep: 0.01 }}
					pinch={{ step: 0.2 }}
				>
					<div
						className={`${modalStyles.contentContainer} ${styles.imageContentContainer}`}
					>
						<div className={styles.imageContainer}>
							{/* This component has a wrapperClass prop but its internal classes take precedence, rendering it useless.
								  Using inline styles to ensure they get applied. */}
							<TransformComponent wrapperStyle={{ margin: '0 auto' }}>
								{activeImage.isOpenAccess ? (
									/* eslint-disable @next/next/no-img-element */
									<img
										onLoad={handleImageLoaded}
										alt={activeImage.altText}
										src={activeImage.originalImageUrl}
										loading="eager"
										className={styles.image}
									/>
								) : (
									<img
										onLoad={handleImageLoaded}
										alt={activeImage.altText}
										src={activeImage.webImageUrl}
										loading="eager"
										className={`${styles.image} ${styles.restricted}`}
									/>
									/* eslint-enable @next/next/no-img-element */
								)}
							</TransformComponent>

							{!activeImage.isOpenAccess && (
								<div className={styles.rightsMessage}>
									<RightsRestrictedMessage
										color="var(--color-white)"
										backgroundColor="var(--color-met-red-medium)"
									/>
								</div>
							)}
						</div>

						{activeImage?.publicCaption && (
							<div className={styles.caption}>{activeImage.publicCaption}</div>
						)}

						<div className={modalStyles.controls}>
							<ModalCloseButton handleClick={onClose} />
							{!forceNoDownloadButton && (
								<ModalDownloadButton
									disabled={!activeImage.isOpenAccess}
									imageUrl={activeImage.originalImageUrl}
									objectID={objectID}
									objectTitle={objectTitle}
								/>
							)}
							{!forceNoZoom && (
								<ZoomControls isDisabled={!activeImage.isOpenAccess} />
							)}
							{images.length > 1 && (
								<PaginationControls
									handleBackArrowClick={handleBackArrowClick}
									handleForwardArrowClick={handleForwardArrowClick}
									isRightToLeft={isRightToLeft}
									nextButtonRef={nextButtonRef}
									previousButtonRef={previousButtonRef}
									showNextButton={showNextButton}
									showPreviousButton={showPreviousButton}
								/>
							)}
						</div>
					</div>
				</TransformWrapper>
			</div>
		</ModalBackground>
	);
};

export default ModalCollectionViewer;

export interface IProps {
	activeIndex: number;
	/** @remarks: Online Learning doesn't have downloadable images */
	forceNoDownloadButton?: boolean;
	/** @remarks: Online Learning doesn't have zoomable images */
	forceNoZoom?: boolean;
	images: ImageViewerImage[];
	isRightToLeft: boolean;
	objectID?: string | number;
	objectTitle: string;
	onClose: () => void;
	onIndexChange: Dispatch<SetStateAction<number>>;
	ref?: any;
}
